"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _classnames = _interopRequireDefault(require("classnames"));
var _text = _interopRequireDefault(require("@ux/text"));
var _feedbackExpressions = _interopRequireDefault(require("@ux/intents/lib/feedback-expressions"));
/**
 * @typedef {import('react').ReactElement} ReactElement
 */

const UX_CIRCULAR_PROGRESS_CLASSNAME = 'ux-circular-progress';
const emphasisOptions = Object.values(_feedbackExpressions.default).map(v => v.toLowerCase());
const sizeOptions = ['sm', 'md', 'lg', 'xl'];

/**
 * We automatically apply ARIA attributes unless explicitly meant to be omitted.
 * This is meant for cases where the attributes are meant to be placed on another element.
 *
 * If the given `aria-valuemin=null`, do not set `aria-valuemin` at all.
 * Otherwise, set with whatever else is provided to `aria-valuemin` or the given `min`.
 *
 * @param {Number} value - The value of min, max, or value
 * @param {String} ariaAttr - The attribute to finally write
 * @param {Number} ariaValue - The value to finally write
 * @returns {Object} A spreadable object of attributes
 */
function explicitAria(value, ariaAttr, ariaValue) {
  // Checking for explicit null, not undefined
  // eslint-disable-next-line eqeqeq
  return ariaValue === null ? {
    [ariaAttr]: null
  } : {
    [ariaAttr]: ariaValue || value
  };
}

/**
 * Round value to given step
 *
 * @param {Number} val - Current value
 * @param {Number} step - Step to round to
 * @returns {Number} The updated amount
 */
function roundToStep(val, step) {
  return isNaN(val) ? val : Math.round(val / step) * step;
}

/**
 * Creates a <circle/> shape.
 *
 * @param {Object} props - Configuration Object
 * @param {String} [props.className] - Classname for the component.
 * @param {Number} [props.start] - Starting value.
 * @param {Number} [props.end] - Ending value.
 * @param {Number} [props.min] - Minimum value.
 * @param {Number} [props.max] - Maximum value.
 * @returns {ReactElement} <Circle/> for use within <svg/>
 */
function Circle({
  className,
  start,
  end,
  min,
  max
}) {
  const length = max - min;
  const strokeDashValues = [length];
  if (!isNaN(end)) {
    strokeDashValues.unshift(end - min);
  }
  if (![start, end].some(isNaN) && start !== end) {
    // Render segmented display
    const offset = start - min;
    strokeDashValues[0] -= offset;
    strokeDashValues.unshift(offset);
    strokeDashValues.unshift(0);
  }
  return /*#__PURE__*/_react.default.createElement("circle", {
    className: className,
    cx: "36",
    cy: "36",
    r: "30",
    fill: "none",
    strokeWidth: "6",
    pathLength: length,
    strokeDasharray: strokeDashValues.join(' '),
    style: {
      strokeLinecap: isNaN(end) ? 'round' : 'butt'
    }
  });
}
Circle.propTypes = {
  className: _propTypes.default.string,
  start: _propTypes.default.number,
  end: _propTypes.default.number,
  min: _propTypes.default.number,
  max: _propTypes.default.number,
  step: _propTypes.default.number
};

/**
 * Creates a semantic representation of the progress
 *
 * @param {Object} props - Configuration object
 * @param {String} props.type - Type of component
 * @param {Number} props.value - Value of the progress
 * @returns {ReactElement} React Component
 */
function ValueElement({
  type,
  value,
  ...props
}) {
  const Tag = type === 'range' ? 'input' : type;
  const val = isNaN(value) ? '' : value;
  return /*#__PURE__*/_react.default.createElement(Tag, (0, _extends2.default)({}, props, {
    value: val,
    type: type === 'range' ? 'range' : null
  }));
}

/**
 * Create a CircularProgress component.
 *
 * @type {import('@ux/circular-progress').CircularProgressComponent}
 */
const CircularProgress = /*#__PURE__*/_react.default.forwardRef(function CircularProgress({
  className,
  children,
  disabled,
  emphasis,
  id,
  label,
  helpMessage,
  min = 0,
  max = 100,
  name,
  output,
  rotation,
  size: shirtSize = 'md',
  step = 1,
  type = 'progress',
  value,
  ...rest
}, ref) {
  // style is explicitly omitted, do not override.
  delete rest.style;

  // If value is undefined (not set)
  // This array is a length === 1 of a single undefined value -> [undefined]
  // This allows the ability for elements to render even if no value is explicitly provided.
  const _values = [].concat(value).map(val => roundToStep(val, step));

  // type=meter does not allow non-zero minimum
  const minAdjust = type !== 'meter' ? min : 0;
  const minRound = roundToStep(minAdjust, step);
  const maxRound = roundToStep(max, step);
  const startValue = Math.min(..._values);
  const endValue = Math.max(..._values);
  const textSize = sizeOptions.indexOf(shirtSize) - 1;
  const style = {
    '--ux-circular-progress-rotation': rotation
  };

  // Create a collection of ids for accessibility wiring
  const idxs = _values.map((_, index) => `${id}-${index}`);
  const contents = /*#__PURE__*/_react.default.createElement("div", {
    className: `${UX_CIRCULAR_PROGRESS_CLASSNAME}-children`
  }, children);
  const values = /*#__PURE__*/_react.default.createElement("div", {
    className: `${UX_CIRCULAR_PROGRESS_CLASSNAME}-values`
  }, _values.map((val, index) => {
    return /*#__PURE__*/_react.default.createElement(ValueElement, (0, _extends2.default)({
      type: type,
      id: idxs[index],
      key: idxs[index],
      name: name,
      className: `${UX_CIRCULAR_PROGRESS_CLASSNAME}-value`,
      min: minRound,
      max: maxRound,
      value: val
    }, explicitAria(minAdjust, 'aria-valuemin', rest['aria-valuemin']), explicitAria(max, 'aria-valuemax', rest['aria-valuemax']), explicitAria(val, 'aria-valuenow', rest['aria-valuenow'])));
  }));
  const classNames = (0, _classnames.default)(UX_CIRCULAR_PROGRESS_CLASSNAME, className);
  return /*#__PURE__*/_react.default.createElement("div", (0, _extends2.default)({}, rest, {
    "aria-disabled": disabled,
    className: classNames,
    "data-version": "2500",
    style: style
  }), /*#__PURE__*/_react.default.createElement("div", {
    className: `${UX_CIRCULAR_PROGRESS_CLASSNAME}-display`,
    ref: ref
  }, /*#__PURE__*/_react.default.createElement("svg", {
    className: `${UX_CIRCULAR_PROGRESS_CLASSNAME}-svg`,
    role: "presentation",
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 72 72"
  }, /*#__PURE__*/_react.default.createElement(Circle, {
    className: `${UX_CIRCULAR_PROGRESS_CLASSNAME}-track`,
    min: minRound,
    max: maxRound,
    step: step
  }), /*#__PURE__*/_react.default.createElement(Circle, {
    className: (0, _classnames.default)(`${UX_CIRCULAR_PROGRESS_CLASSNAME}-fill`, {
      [`${UX_CIRCULAR_PROGRESS_CLASSNAME}--emphasis-${emphasis}`]: emphasisOptions.includes(emphasis)
    }),
    min: minRound,
    max: maxRound,
    step: step,
    start: startValue,
    end: endValue
  })), /*#__PURE__*/_react.default.createElement(_text.default.output, {
    className: `${UX_CIRCULAR_PROGRESS_CLASSNAME}-output`,
    size: textSize,
    text: output
  }), children ? contents : null), label ? /*#__PURE__*/_react.default.createElement(_text.default.label, {
    as: "label",
    size: textSize,
    text: label
  }) : null, helpMessage ? /*#__PURE__*/_react.default.createElement(_text.default.span, {
    as: "caption",
    size: textSize,
    emphasis: "passive",
    text: helpMessage
  }) : null, !isNaN(endValue) ? values : null);
});
CircularProgress.propTypes = {
  /** Optional class name for positioning */
  className: _propTypes.default.string,
  /** Nodes to appear within the circle */
  children: _propTypes.default.node,
  /** Set disabled opacity */
  disabled: _propTypes.default.bool,
  /** Help message  */
  helpMessage: _propTypes.default.node,
  /** Used to uniquely identify the component */
  id: _propTypes.default.string,
  /** Used to connect for FormData */
  name: _propTypes.default.string,
  /** Node to render below the circle */
  label: _propTypes.default.node,
  /** Node to render inside the circle */
  output: _propTypes.default.node,
  /** One of the feedback expressions from `@ux/intents`, eg., 'warning' */
  emphasis: _propTypes.default.oneOf(emphasisOptions),
  /** Default is 0, the minimum value. */
  min: _propTypes.default.number,
  /** Default is 100, the maximum value. */
  max: _propTypes.default.number,
  /** Additive CSS rotation for value starting point */
  rotation: _propTypes.default.string,
  /** A T-shirt size for the component */
  size: _propTypes.default.oneOf(sizeOptions),
  /** Default is 1, the distance between values. */
  step: _propTypes.default.number,
  /** The value to represent in the track. If given an array,
   * it will only visually show the smallest and larger values provided. */
  value: _propTypes.default.oneOfType([_propTypes.default.number, _propTypes.default.arrayOf(_propTypes.default.number)]),
  /** Default is progress, the element represented as HTML,
   * can also accept 'meter' for `<meter/>` and 'range' for `<input type="range"/>`. */
  type: _propTypes.default.oneOf(['progress', 'meter', 'range'])
};
var _default = exports.default = CircularProgress;